import { uz } from './locales/uz';
import { ru } from './locales/ru';
import { kk } from './locales/kk';
import { es } from './locales/es';
import { esNoTelegram } from './locales/es.no-telegram';
import { esCl } from './locales/es.cl';
import { ruKz } from './locales/ru.kz';
import { countryConfig } from '../country';

// 'kk' (Kazakh) is a KZ-only locale — KZ ships `locales: ['ru', 'kk']`, UZ ships
// ['uz', 'ru'], ES (MiLicita) ships ['es']. Every catalog is bundled, but LOCALES
// (from the active country) decides which the deployment actually routes/renders.
export type Locale = 'uz' | 'ru' | 'kk' | 'es';
export const LOCALES = [...countryConfig.locales] as Locale[];
// The audience is Russian-dominant, so ru is the default UI language (guests
// with no stored/browser preference) and the fallback catalog.
export const DEFAULT_LOCALE = countryConfig.defaultLocale as Locale;

type Catalog = Record<string, string>;

// `ru` serves both deployments. On Kazakhstan the sentences that are only true
// in Uzbekistan (payment providers, «на узбекском», Uzbek portals, Telegram
// alerts) are replaced from `locales/ru.kz.ts`; on Uzbekistan the catalog is
// `ru` itself, the same object as before. See that file for the rules.
const ruForCountry: Catalog = countryConfig.code === 'kz' ? { ...ru, ...ruKz } : ru;

// `es` is Spain's. While Spain runs no Telegram bot, the sentences that
// promise Telegram on a surface that renders anyway are replaced from
// `locales/es.no-telegram.ts`; once `messaging.telegram` is on, `es` is itself.
// Chile (Liciora) renders the same Spanish catalog with no bot at all, and the
// sentences only true in Spain (euros, NIF, PLACSP, pliegos, comunidades
// autónomas…) said for Chile from `locales/es.cl.ts`. UZ and KZ never render the
// `es` catalog, so neither is affected; Spain's own value is unchanged.
const esForCountry: Catalog =
  countryConfig.code === 'cl'
    ? { ...es, ...esNoTelegram, ...esCl }
    : countryConfig.code === 'es' && !countryConfig.messaging.telegram
      ? { ...es, ...esNoTelegram }
      : es;

const catalogs: Record<Locale, Catalog> = { uz, ru: ruForCountry, kk, es: esForCountry };

// Brand identity is interpolated into copy via {{brand}} / {{brandEmail}} /
// {{brandHost}} tokens so the catalogs hold NO hardcoded UzTender/uztender.com
// string — the KZ build renders KazTender.com / kaztender.com from the same
// source strings. These are ALWAYS merged into the interpolation params (even
// when a caller passes none), so a bare `translate(locale, key)` still resolves
// the brand tokens. UZ values equal the former literals, so UZ copy is unchanged.
const BRAND_TOKENS: Record<string, string> = {
  brand: countryConfig.brand.name,
  brandEmail: countryConfig.brand.supportEmail,
  brandHost: new URL(countryConfig.brand.siteUrl).host,
  // Company-id term + digit count, so copy that names it renders the country's
  // own scheme: UZ «ИНН» / 9, KZ «БИН/ИИН» / 12. UZ values equal the former
  // literals, so UZ copy is unchanged.
  tin: countryConfig.companyIdForm.label,
  tinDigits: String(countryConfig.companyIdForm.digits),
};

export function translate(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>,
): string {
  const catalog = catalogs[locale];
  const fallbackCatalog = catalogs[DEFAULT_LOCALE];
  let value = catalog[key] ?? fallbackCatalog[key] ?? key;
  // {{currency}} is locale-dependent (ru «сум»/«тенге», uz «so'm»), so it is
  // computed here rather than in the static BRAND_TOKENS.
  const currency =
    countryConfig.currency.words[locale] ?? countryConfig.currency.words[DEFAULT_LOCALE] ?? '';
  // Country name in three cases (also locale-dependent) — {{country}} nominative,
  // {{countryGen}} genitive, {{countryPrep}} prepositional.
  const cn = countryConfig.countryName[locale] ?? countryConfig.countryName[DEFAULT_LOCALE];
  const merged = {
    ...BRAND_TOKENS,
    currency,
    country: cn?.nom ?? '',
    countryGen: cn?.gen ?? '',
    countryPrep: cn?.prep ?? '',
    ...(params ?? {}),
  };
  for (const [k, v] of Object.entries(merged)) {
    /*
     * `replaceAll`, not `replace`. With a STRING needle `replace` swaps only
     * the FIRST occurrence, so a phrase naming the same value twice rendered
     * the second one as a literal `{{name}}`.
     *
     * Shipped that way to production on 2026-08-14 in `invoice.note`
     * («Тариф Бизнес активируется… Назначение платежа: оплата подписки
     * {{name}}») — on the printable invoice, in the sentence telling the
     * buyer what to write as the payment purpose. Caught by looking at the
     * rendered page, not by any test: every existing string happened to use
     * each placeholder once, so nothing exercised the second occurrence.
     *
     * A string needle is also why this is safe: no regex escaping, no
     * special meaning for `$&` in the replacement.
     */
    value = value.replaceAll(`{{${k}}}`, String(v));
  }
  return value;
}

/**
 * The URL prefixes that select a locale. UZ and KZ read the historical three,
 * whatever they route (`/kk/<typo>` on UZ still answers in Kazakh — unchanged).
 * Spain and Chile route `es` alone, on bare paths: there a `/uz/…`, `/kk/…` or
 * `/ru/…` URL is a Spanish 404 like any other mistyped address, never the
 * Uzbek, Kazakh or Russian app chrome under milicita.es or liciora.cl.
 */
const PATH_LOCALES: readonly Locale[] =
  countryConfig.code === 'es' || countryConfig.code === 'cl' ? [] : ['ru', 'uz', 'kk'];

export function resolveLocaleFromPath(pathname: string): Locale {
  const segment = pathname.split('/')[1] as Locale;
  if (PATH_LOCALES.includes(segment)) return segment;
  return DEFAULT_LOCALE;
}

export function getLocaleFromStorage(): Locale | null {
  // SSR-safe: check for window
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem('locale');
    if (stored === 'uz' || stored === 'ru') return stored as Locale;
  } catch {
    // ignore
  }
  return null;
}

export function setLocaleInStorage(locale: Locale): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('locale', locale);
  } catch {
    // ignore
  }
}
